/**
 * **The receding-horizon autoplay policy** — plays one combatant's ENTIRE activation to
 * `end_turn` by repeatedly asking the utility advisor (`rankMacroMoves`) for its best next
 * macro-move, submitting it through an abstract `MatchHandle` (`./matchHandle.ts`), observing
 * the AUTHORITATIVE resulting state (real dice — never resolved locally), and re-planning
 * against it. Transport-agnostic: this file never imports anything ws-shaped, so the SAME loop
 * composes over a direct-referee `MatchHandle` with zero refactor — the seam's whole point.
 *
 * **Termination.** Each iteration re-ranks; when the top plan is a bare "stay, no attack" (the
 * generator's do-nothing sentinel — nothing scored better than holding position) or no legal
 * macro-move exists at all, the policy submits `end_turn` and stops. A hard `maxSteps` ceiling
 * (default {@link DEFAULT_MAX_STEPS}) guards against a pathological non-terminating loop — move
 * budget strictly decreases every move leg, so a healthy fight converges well under it.
 *
 * **Answers prompts via a pluggable `promptPolicy`** (`./promptPolicy.ts`) — every
 * submission routes through {@link submitResolvingPrompts}, which calls `promptPolicy` on a
 * `prompt` observation and, when it resolves the whole chain, folds the resolved observation
 * back in and keeps going. Defaults to {@link answerPrompt} (the shipped Brace/Overwatch-
 * checkpoint/variable-damage-type policy); pass `promptPolicy: undefined`-shaped no-op (or any
 * other `PromptPolicy`) to opt out. Only when the policy itself declines/can't classify a prompt
 * (`kind: "unhandled"`) does the loop halt (`kind: "prompted"`) rather than guessing an answer.
 * **The footgun this guards:** the wire's mutating verbs do NOT fail loudly on a pending prompt,
 * so every submission here checks `observation.prompt` explicitly — never inferred from the
 * absence of a thrown error.
 *
 * **Chooses via a pluggable `moveSelector`** (`./moveSelector.ts`) — every iteration ranks
 * `rankMacroMoves`'s top-k, then hands that list to `moveSelector` rather than hardcoding
 * `ranked[0]`. Defaults to {@link argmaxMoveSelector}; the Monte-Carlo sweep injects
 * `epsilonGreedySelector` for optional policy-noise runs. Threads `autoplayActivation` →
 * `autoplaySide` (this file) →
 * `runMatch` (`./matchRunner.ts`) → `simulateFight` (`./simulateFight.ts`), which is where a
 * sweep actually wires a non-default selector in.
 */
import { activeAttack, sideOf, type EncounterState, type Side } from "@lancer-vtt/domain";
import type { Compendium } from "@lancer-vtt/compendium";
import { lockPairingBlock, SEED_OVERLAY, type LoadedOverlay } from "@lancer-vtt/rules";
import type { EncounterIntent, PromptRequest } from "@lancer-vtt/shared";
import { pickActivation } from "./activationPick";
import { rankMacroMoves, type RankedMacroMove } from "./advisor";
import type { FeatureTerm } from "./evaluator";
import type { MatchHandle, MatchObservation, MatchOp } from "./matchHandle";
import { argmaxMoveSelector, type MoveSelector } from "./moveSelector";
import { timePlanning } from "./perfHooks";
import { answerPrompt, type PromptPolicyOutcome } from "./promptPolicy";

/** The pluggable prompt-answering seam `autoplayActivation` calls on every `prompt` halt — `./promptPolicy.ts`'s {@link answerPrompt} signature (a human-driven caller or the direct-referee handle can supply a different one, or none). */
export type PromptPolicy = (args: { handle: MatchHandle; observation: MatchObservation; overlay?: LoadedOverlay }) => Promise<PromptPolicyOutcome>;

/** Submit `op`; if it halts on a prompt, hand it to `promptPolicy` — resolved, fold the final observation back in; unhandled, return the ORIGINAL prompted observation unchanged so the caller's halt-and-surface logic fires. (Args bundled into one object — `CODE_STYLE.md`'s max-params-3 discipline.) */
async function submitResolvingPrompts(args: {
  handle: MatchHandle;
  op: MatchOp;
  promptPolicy: PromptPolicy;
  overlay: LoadedOverlay;
}): Promise<MatchObservation> {
  const { handle, op, promptPolicy, overlay } = args;
  const obs = await handle.submit(op);
  if (!obs.prompt) return obs;
  const outcome = await promptPolicy({ handle, observation: obs, overlay });
  return outcome.kind === "resolved" ? outcome.observation : obs;
}

/** Safety-valve ceiling on policy iterations (each iteration submits up to one move + one attack leg). Move budget strictly decreases per move leg, so a real fight terminates far below this. */
export const DEFAULT_MAX_STEPS = 40;

/** One submission the policy made — the caller's play-by-play. */
export type AutoplayStep = {
  /** The ranked plan this step executed. Absent only for a terminal `end_turn` submitted with zero legal macro-moves available (not even "stay"). */
  plan?: RankedMacroMove;
  /** Which leg of the plan this submission was. */
  op: "move" | "attack" | "end_turn";
  observation: MatchObservation;
};

export type AutoplayOutcome =
  /** Reached `end_turn` with every submission applied cleanly (no prompt, no rejection). */
  | { kind: "completed"; steps: AutoplayStep[] }
  /** `unitId` was never this activation's acting combatant when autoplay started — nothing was submitted. Distinguished from `completed` (zero steps here vs. at least the closing `end_turn` there). */
  | { kind: "not_acting"; steps: AutoplayStep[] }
  /** Halted on an unanswered `needs_decision`. `steps` includes the halting submission; answering it means re-submitting the same intent with `choices[promptId]` set, via `MatchHandle.submit` — see `./matchHandle.ts`'s header. */
  | { kind: "prompted"; steps: AutoplayStep[]; prompt: PromptRequest }
  /** A submission was rejected outright. Should not happen — the generator only ever proposes moves it proved legal against the pre-submission state (`packages/ai/src/legality.ts`) — so a real occurrence is a genuine bug worth surfacing loudly, never silently retried or swallowed. */
  | { kind: "rejected"; steps: AutoplayStep[]; rejection: { reason: string; category?: string } }
  /** Hit `maxSteps` without reaching `end_turn` — the safety valve tripped; treat `steps` as a diagnostic trace, not a completed activation. */
  | { kind: "max_steps"; steps: AutoplayStep[] };

export type AutoplayArgs = {
  handle: MatchHandle;
  /** The combatant whose activation to autoplay (the advisor's `actorId` — a unit id, distinct from `handle.actorId`, the driving IDENTITY the submissions are attributed as). */
  unitId: string;
  compendium: Compendium;
  overlay?: LoadedOverlay;
  /** Forwarded to `rankMacroMoves`; defaults to `DEATHMATCH_TERMS`. */
  terms?: readonly FeatureTerm[];
  /** Forwarded to `generateMacroMoves`'s destination pruning via `rankMacroMoves`; default 10. */
  maxDestinations?: number;
  maxSteps?: number;
  /** The prompt-answering policy every submission routes through (this file's header doc); defaults to `./promptPolicy.ts`'s {@link answerPrompt}. */
  promptPolicy?: PromptPolicy;
  /** The move-SELECTION policy (`./moveSelector.ts`) — which of `rankMacroMoves`'s scored top-k this activation actually plays. Defaults to {@link argmaxMoveSelector} (always the top-ranked plan); the Monte-Carlo sweep injects `epsilonGreedySelector` here for policy-noise runs. */
  moveSelector?: MoveSelector;
};

/** How many top-ranked plans `rankMacroMoves` returns for `moveSelector` to choose among — `rankMacroMoves`'s own default `k` (5). A `moveSelector` needs more than the bare argmax to have a genuine "other than argmax" pool to draw from; the default {@link argmaxMoveSelector} only ever reads index 0 regardless, so this is unaffected by (and does not affect) the dice-only default path. */
const SELECTOR_TOP_K = 5;

/** Autoplay `unitId`'s current activation to completion (`end_turn`) or a halt — the policy's one entry point. */
export async function autoplayActivation(args: AutoplayArgs): Promise<AutoplayOutcome> {
  const {
    handle,
    unitId,
    compendium,
    overlay = SEED_OVERLAY,
    terms,
    maxDestinations,
    maxSteps = DEFAULT_MAX_STEPS,
    promptPolicy = answerPrompt,
    moveSelector = argmaxMoveSelector,
  } = args;
  const steps: AutoplayStep[] = [];
  let state = await handle.getState();

  for (let i = 0; i < maxSteps; i++) {
    const turn = state.activation.turn;
    if (turn.phase !== "acting" || turn.actor !== unitId) {
      return { kind: steps.length === 0 ? "not_acting" : "completed", steps };
    }

    // `timePlanning` (`./perfHooks.ts`) is a no-op wrapper unless explicitly enabled — a single
    // boolean check when disabled.
    const ranked = timePlanning(() =>
      rankMacroMoves({
        state,
        actorId: unitId,
        compendium,
        overlay,
        k: SELECTOR_TOP_K,
        ...(terms ? { terms } : {}),
        ...(maxDestinations !== undefined ? { maxDestinations } : {}),
      }),
    );
    // The widened context (`./moveSelector.ts`'s header) — the argmax/epsilon-greedy defaults
    // read only `ranked`; a search-backed selector needs the rest to generate the opponent's own
    // reply moves, which `ranked` alone never carries.
    const top = moveSelector({
      ranked,
      state,
      actorId: unitId,
      compendium,
      overlay,
      ...(terms ? { terms } : {}),
      ...(maxDestinations !== undefined ? { maxDestinations } : {}),
    });

    // Nothing worth doing this iteration (either no legal macro-move at all, or the
    // top-ranked one is the generator's bare "stay, no attack" sentinel) — close the
    // activation out.
    if (!top || (!top.move.moved && !top.move.attack)) {
      const obs = await submitResolvingPrompts({
        handle,
        op: { kind: "command", command: { type: "end_turn", actorId: handle.actorId, combatantId: unitId } },
        promptPolicy,
        overlay,
      });
      steps.push({ ...(top ? { plan: top } : {}), op: "end_turn", observation: obs });
      if (obs.prompt) return { kind: "prompted", steps, prompt: obs.prompt };
      if (obs.rejection) return { kind: "rejected", steps, rejection: obs.rejection };
      return { kind: "completed", steps };
    }

    if (top.move.moveIntent) {
      const obs = await submitResolvingPrompts({ handle, op: { kind: "intent", intent: top.move.moveIntent }, promptPolicy, overlay });
      steps.push({ plan: top, op: "move", observation: obs });
      if (obs.prompt) return { kind: "prompted", steps, prompt: obs.prompt };
      if (obs.rejection) return { kind: "rejected", steps, rejection: obs.rejection };
      state = obs.state;
    }

    if (top.move.attack) {
      // A multi-attack weapon ("can make three attacks at a time") keeps its attack lock open after
      // each attack while it has attacks left; make them all at the same target while it stands.
      for (let attack: EncounterIntent | undefined = top.move.attack.intent; attack !== undefined; attack = nextAttackOf(state, attack)) {
        const obs = await submitResolvingPrompts({ handle, op: { kind: "intent", intent: attack }, promptPolicy, overlay });
        steps.push({ plan: top, op: "attack", observation: obs });
        if (obs.prompt) return { kind: "prompted", steps, prompt: obs.prompt };
        if (obs.rejection) return { kind: "rejected", steps, rejection: obs.rejection };
        state = obs.state;
      }
    }
  }
  return { kind: "max_steps", steps };
}

/**
 * The same attack again when `attack`'s weapon is a multi-attack weapon with an attack left on the
 * open lock ({@link lockPairingBlock}) and its unit target is still standing; else `undefined`.
 */
function nextAttackOf(state: EncounterState, attack: EncounterIntent): EncounterIntent | undefined {
  if (attack.type !== "request_attack" || attack.weapon === undefined || attack.target.kind !== "unit") return undefined;
  const lock = activeAttack(state.activation);
  if (lock === null || !lock.weapons.includes(attack.weapon)) return undefined;
  if (lockPairingBlock({ state, attackerId: attack.attacker, weapon: attack.weapon, lock }) !== undefined) return undefined;
  const target = state.combatants[attack.target.id];
  return target !== undefined && target.current.hp > 0 ? attack : undefined;
}

/**
 * **Activation-pick, composed over `autoplayActivation`** — drives an entire SIDE's activations
 * this round: while `side` still has an able un-acted unit and it is genuinely `side`'s turn to
 * activate one (nobody mid-turn ∧ `activation.activeSide === side`), it picks which unit via the
 * thin argmax {@link "./activationPick" pickActivation} (of the units `side` could still
 * activate, whichever one's own best plan scores highest — this is NOT a search over activation
 * order; see that file's header for the documented simplification), submits `activate_unit` for
 * it, then runs {@link autoplayActivation} on it
 * to `end_turn`. Repeats until control passes to the other side (or the round completes),
 * `side` has nothing left to activate, or one activation halts on a prompt/rejection.
 *
 * If the loop is started while a `side` combatant is ALREADY mid-turn (a resumed/partial
 * activation from a prior call), it drives THAT activation first via `autoplayActivation`
 * rather than re-picking — `pickActivation` is only consulted from `turn.phase === "idle"`.
 *
 * Composed over the SAME transport-agnostic `MatchHandle` seam as `autoplayActivation` — never
 * ws-concrete, so `combat-autoresolution`'s direct-referee handle can reuse this unchanged.
 */
export type AutoplaySideOutcome =
  /** Every activation this call made reached `end_turn` cleanly, and control passed away from `side` (or the round completed). `activations` may be empty if `side` had nothing to activate but IS mid-way through the round (rare — `not_acting` covers the common "nothing to do" case). */
  | { kind: "completed"; activations: AutoplayOutcome[] }
  /** `side` had no able un-acted unit to activate (or it wasn't `side`'s turn to pick one) when the loop started — zero activations attempted. */
  | { kind: "not_acting"; activations: AutoplayOutcome[] }
  /** One activation (or the `activate_unit` submission that opened it) halted before `end_turn` — `cause` is that activation's own non-`"completed"` outcome (`prompted`/`rejected`/`max_steps`/`not_acting`), and `activations` holds every PRIOR activation that completed cleanly. */
  | { kind: "halted"; activations: AutoplayOutcome[]; cause: AutoplayOutcome }
  /** Hit `maxActivations` without control passing away from `side` — the safety valve tripped. */
  | { kind: "max_activations"; activations: AutoplayOutcome[] };

/** Safety-valve ceiling on how many units one `autoplaySide` call will activate — a real fight's side has far fewer than this many un-acted units at once (`ableUnactedOnSide` strictly shrinks every `end_turn`), so a healthy call terminates well under it. */
export const DEFAULT_MAX_ACTIVATIONS = 12;

export type AutoplaySideArgs = {
  handle: MatchHandle;
  side: Side;
  compendium: Compendium;
  overlay?: LoadedOverlay;
  /** Forwarded to `pickActivation` and every `autoplayActivation` call; defaults to `DEATHMATCH_TERMS`. */
  terms?: readonly FeatureTerm[];
  /** Forwarded to `pickActivation`'s and `autoplayActivation`'s destination pruning; default 10. */
  maxDestinations?: number;
  /** Forwarded to each `autoplayActivation` call's own per-unit step ceiling; defaults to `DEFAULT_MAX_STEPS`. */
  maxSteps?: number;
  /** Ceiling on how many units THIS call will activate; defaults to `DEFAULT_MAX_ACTIVATIONS`. */
  maxActivations?: number;
  /** Forwarded to every `autoplayActivation` call; defaults to `./promptPolicy.ts`'s `answerPrompt`. */
  promptPolicy?: PromptPolicy;
  /** Forwarded to every `autoplayActivation` call (`./moveSelector.ts`'s header doc); defaults to {@link argmaxMoveSelector}. */
  moveSelector?: MoveSelector;
};

/** Drive `side`'s activations to completion (control passing away, or the round ending) — the activation-pick policy's one entry point. */
export async function autoplaySide(args: AutoplaySideArgs): Promise<AutoplaySideOutcome> {
  const { handle, side, compendium, overlay = SEED_OVERLAY, terms, maxDestinations, maxSteps, maxActivations = DEFAULT_MAX_ACTIVATIONS, promptPolicy, moveSelector } = args;
  const activations: AutoplayOutcome[] = [];

  for (let i = 0; i < maxActivations; i++) {
    const state = await handle.getState();
    const turn = state.activation.turn;

    let unitId: string;
    if (turn.phase === "acting") {
      const actor = state.combatants[turn.actor];
      if (!actor || sideOf(actor) !== side) {
        return { kind: activations.length === 0 ? "not_acting" : "completed", activations };
      }
      unitId = turn.actor;
    } else {
      if (state.activation.activeSide !== side) {
        return { kind: activations.length === 0 ? "not_acting" : "completed", activations };
      }
      const picked = pickActivation({
        state,
        side,
        compendium,
        overlay,
        ...(terms ? { terms } : {}),
        ...(maxDestinations !== undefined ? { maxDestinations } : {}),
      });
      if (picked === null) {
        return { kind: activations.length === 0 ? "not_acting" : "completed", activations };
      }
      const obs = await handle.submit({ kind: "command", command: { type: "activate_unit", actorId: handle.actorId, combatantId: picked } });
      if (obs.prompt) return { kind: "halted", activations, cause: { kind: "prompted", steps: [], prompt: obs.prompt } };
      if (obs.rejection) return { kind: "halted", activations, cause: { kind: "rejected", steps: [], rejection: obs.rejection } };
      unitId = picked;
    }

    const outcome = await autoplayActivation({
      handle,
      unitId,
      compendium,
      overlay,
      ...(terms ? { terms } : {}),
      ...(maxDestinations !== undefined ? { maxDestinations } : {}),
      ...(maxSteps !== undefined ? { maxSteps } : {}),
      ...(promptPolicy !== undefined ? { promptPolicy } : {}),
      ...(moveSelector !== undefined ? { moveSelector } : {}),
    });
    activations.push(outcome);
    if (outcome.kind !== "completed") {
      return { kind: "halted", activations, cause: outcome };
    }
  }
  return { kind: "max_activations", activations };
}
